import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Typography } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { useNavigate } from "react-router-dom";
import { CalendarEventType } from "../../common/const";
import moment from "moment";
import ViewEventDialog from "../Calendar/ViewEventDialog";
import { ArrowDropDown } from "@mui/icons-material";
import CommonEventIcon from "../../common/components/CommonEventIcon";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { getCalendarEvent } from "../../api/calendarApi";

const Calendar = ({
    fromSat,
    testType
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [days, setDays] = useState([]);
    const [eventDate, setEventDate] = useState(null);
    const [allEvent, setAllEvent] = useState([]);
    const [events, setEvents] = useState([]);
    const [viewOpen, setViewOpen] = useState(false);
    const [viewData, setViewData] = useState(null);

    const getNextSevenDays = () => {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            dates.push(moment().add(i, 'days').format('YYYY-MM-DD'));
        }
        setDays(dates);
        setEventDate(dates[0]);
    };

    useEffect(() => {
        getNextSevenDays();
    }, []);

    const getEvents = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getCalendarEvent(user?.user_id, testType || '');
            setAllEvent(response.data);
            setEvents(response.data.filter((a) => a.event_start_timestamp.startsWith(eventDate)));
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.user_id) {
            getEvents();
        }
    }, [user?.user_id, eventDate, testType])

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            {viewOpen && (
                <ViewEventDialog
                    viewOpen={viewOpen}
                    setViewOpen={setViewOpen}
                    viewData={viewData}
                    eventList={events}
                />
            )}
            <CommonCard
                borderRadius={2}
                border={fromSat ? `1px solid #0000001A` : 'none'}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>{fromSat ? `Upcoming ${testType} Tests` : 'Next Week Schedule'}</Typography>
                                <Typography fontSize={14} color={theme.palette.background.dark}>{fromSat ? `Your upcoming registered ${testType} test dates.` : 'A quick look at your schedule for the coming week.'}</Typography>
                            </Box>
                            <CommonButton
                                label="View More"
                                marginBottom="0px"
                                width="140px"
                                height="30px"
                                background={theme.palette.background.main}
                                color={theme.palette.background.dark}
                                borderRadius={3}
                                endIcon={<ArrowDropDown sx={{ color: theme.palette.background.dark }} />}
                                fontWeight={600}
                                handleClick={() => navigate('/my-dcp-calendar')}
                            />
                        </Box>
                    </Fragment>
                }
                content={
                    <Box>
                        <Box
                            display="flex"
                            gap={1}
                            mb={2}
                        >
                            {days.map((a) => (
                                <Box
                                    sx={{
                                        background: eventDate === a ? theme.palette.primary.main : theme.palette.background.main,
                                        borderRadius: 1,
                                        padding: 1,
                                        width: '70px',
                                        cursor: 'pointer',
                                        border: `1px solid ${theme.palette.background.secondary}`
                                    }}
                                    onClick={() => {
                                        setEventDate(a);
                                        setEvents(allEvent.filter((i) => i.event_start_timestamp.startsWith(a)));
                                    }}
                                >
                                    <Typography fontSize={12} fontWeight={600} color={theme.palette.secondary.main} textAlign="center">{moment(a).format('ddd')}</Typography>
                                    <Typography fontSize={18} fontWeight={600} color={theme.palette.secondary.main} textAlign="center">{moment(a).date()}</Typography>
                                </Box>
                            ))}
                        </Box>
                        {events.length > 0 ? events.map((a, index) => {
                            return (
                                <CommonCard
                                    key={index}
                                    background={theme.palette.background.main}
                                    borderRadius={2}
                                    paddingBottom="0px"
                                    padding="13px"
                                    marginTop={index === 0 ? "0px" : "10px"}
                                    content={
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            sx={{
                                                cursor: 'pointer'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setViewOpen(true);
                                                setViewData(a);
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                gap={1}
                                                alignItems="center"
                                            >
                                                {a.event_category === CalendarEventType.application ? (
                                                    <CommonEventIcon background="#E328AF" />
                                                ) : a.event_category === CalendarEventType.exam ? (
                                                    <CommonEventIcon background="#5DCFFF" />
                                                ) : a.event_category === CalendarEventType.task ? (
                                                    <CommonEventIcon background="#00C950" />
                                                ) : a.event_category === CalendarEventType.counselling ? (
                                                    <CommonEventIcon background="#FFAB2D" />
                                                ) : (
                                                    <CommonEventIcon background="#6A7282" />
                                                )}
                                                <Box>
                                                    <Typography fontSize={14} fontWeight="600">{a.event_title}</Typography>
                                                    <Typography fontSize={13} color={theme.palette.background.dark}>{moment.utc(a.event_start_timestamp).format('MMM DD')}</Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Box
                                                    sx={{
                                                        background: a.priority === 'MEDIUM' ? '#FEF9C2' : a.priority === 'HIGH' ? '#FFE2E2' : theme.palette.success.light,
                                                        padding: '3px 15px',
                                                        marginBottom: 0.5,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Typography fontSize={13} color={a.priority === 'MEDIUM' ? '#894B00' : a.priority === 'HIGH' ? 'red' : '#01875F'}>{a.priority}</Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    }
                                />
                            )
                        }) : (
                            <Typography textAlign="center" color={theme.palette.background.dark}>No Event Found</Typography>
                        )}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="15px"
            />
        </Fragment>
    )
}

export default Calendar;